import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { motion } from 'framer-motion';
import { Heart, BookOpen } from 'lucide-react';
import { setCurrentBook } from '../../store/slices/booksSlice';

const DEFAULT_COVER = '/images/default_cover_w299.jpg';

const BookCard = ({ title, author, cover, delay, onClick }) => (
    <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay, duration: 0.4 }}
        whileHover={{ y: -5 }}
        onClick={onClick}
        className="group relative aspect-[2/3] rounded-xl overflow-hidden cursor-pointer bg-slate-800"
    >
        <img
            src={cover || DEFAULT_COVER}
            alt={title}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
            onError={(e) => {
                e.currentTarget.onerror = null;
                e.currentTarget.src = DEFAULT_COVER;
            }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/20 to-transparent opacity-80 group-hover:opacity-90 transition-opacity" />

        <div className="absolute bottom-0 left-0 right-0 p-4 translate-y-2 group-hover:translate-y-0 transition-transform">
            <h4 className="text-white font-bold truncate">{title}</h4>
            <p className="text-slate-300 text-sm truncate">{author}</p>
            <div className="flex items-center justify-between mt-2 opacity-0 group-hover:opacity-100 transition-opacity">
                <div className="flex items-center gap-1 text-red-500 text-xs">
                    <Heart size={12} fill="currentColor" />
                    <span className="font-medium">Coup de coeur</span>
                </div>
            </div>
        </div>
    </motion.div >
);

export default function FavoriteBooks({ onNavigate }) {
    const dispatch = useDispatch();
    const { items: allBooks } = useSelector((state) => state.books);

    const favoriteBooks = [];
    const seenManifestations = new Set();

    for (const book of allBooks) {
        if (book.rating === "true" && !seenManifestations.has(book.manifestation_id)) {
            favoriteBooks.push(book);
            seenManifestations.add(book.manifestation_id);
            if (favoriteBooks.length >= 4) break;
        }
    }

    const handleBookClick = (book) => {
        dispatch(setCurrentBook(book));
        if (onNavigate) {
            onNavigate('Books');
        }
    };

    if (favoriteBooks.length === 0) return null;

    return (
        <div className="bg-slate-900/50 backdrop-blur-xl border border-white/10 rounded-2xl p-6 h-full">
            <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-bold text-white flex items-center gap-2">
                    <Heart size={20} className="text-red-500" />
                    Coups de coeur
                </h3>
                <span className="text-xs text-slate-500 uppercase tracking-wider font-semibold">Sélectionnés pour vous</span>
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                {favoriteBooks.map((book, index) => (
                    <BookCard
                        key={book.id || index}
                        title={book.title}
                        author={book.author}
                        cover={book.cover}
                        delay={index * 0.1}
                        onClick={() => handleBookClick(book)}
                    />
                ))}
            </div>
        </div>
    );
}
